<template>
  <div class="file-template-item">
    <div class="template-name">
      <img :src="templateIcon" alt="" class="template-icon" />
      {{ fileItem.name }}
      <div class="label" v-if="isOnlineDoc(fileItem.url)">在线</div>
    </div>

    <div class="template-btn-list" v-if="fileItem.type !== 'folder'">
      <button
        class="btn blue"
        @click="copyLink"
        v-if="isOnlineDoc(fileItem.url)"
      >
        复制链接
      </button>
      <button
        class="btn blue"
        :class="{ disabled: isSending }"
        @click="sendTemplate"
        :disabled="isSending"
        v-else
      >
        发送
      </button>
    </div>
  </div>
</template>

<script>
import toast from 'src/services/toast.js';
import workWxUtils from 'src/utils/work-wx-utils';
import fileService from 'src/services/file-service';
import fileTemplateListApi from '../../api/file-template-list-api';

export default {
  props: {
    fileItem: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      isSending: false,
    };
  },
  computed: {
    fileNameSuffix() {
      return fileService.getFileNameAndSuffixByUrl(this.fileItem.name).suffix;
    },
    // 只有飞书创建的对应类型fileItem的type才会为对应的，例如飞书创建的表格fileItem的type为sheet，而自定义上传的xlsx等文件type为file
    templateIcon() {
      const defaultIcon =
        'https://pbs-public.oss-cn-shenzhen.aliyuncs.com/static-visa/pdvh5/wwx/file-template-list/default.png';
      const fileTypeConfig = [
        {
          icon: 'https://pbs-public.oss-cn-shenzhen.aliyuncs.com/static-visa/pdvh5/wwx/file-template-list/folder.png',
          fileType: (fileItem) => fileItem.type === 'folder', // 文件夹
        },
        {
          icon: 'https://pbs-public.oss-cn-shenzhen.aliyuncs.com/static-visa/pdvh5/wwx/file-template-list/online-doc.png',
          fileType: (fileItem) =>
            ['docx', 'doc'].some((suff) => suff === fileItem.type), // 在线文档
        },
        {
          icon: 'https://pbs-public.oss-cn-shenzhen.aliyuncs.com/static-visa/pdvh5/wwx/file-template-list/doc.png',
          fileType: (fileItem) => {
            const { suffix } = fileService.getFileNameAndSuffixByUrl(
              fileItem.name,
            );
            return ['docx', 'doc'].some((suff) => suff === suffix);
          }, // 非在线文档
        },
        {
          icon: 'https://pbs-public.oss-cn-shenzhen.aliyuncs.com/static-visa/pdvh5/wwx/file-template-list/pdf.png',
          fileType: (fileItem) => fileService.isPdf(fileItem.name), // pdf
        },
        {
          icon: 'https://pbs-public.oss-cn-shenzhen.aliyuncs.com/static-visa/pdvh5/wwx/file-template-list/jpg.png',
          fileType: (fileItem) => fileService.isImage(fileItem.name), // 图片
        },
        {
          icon: 'https://pbs-public.oss-cn-shenzhen.aliyuncs.com/static-visa/pdvh5/wwx/file-template-list/excel.png',
          fileType: (fileItem) => {
            const { suffix } = fileService.getFileNameAndSuffixByUrl(
              fileItem.name,
            );
            return (
              ['xlsx', 'xls'].some((suff) => suff === suffix) ||
              fileItem.type === 'sheet'
            );
          }, // 表格
        },
      ];

      const currentFileType = fileTypeConfig.find((item) =>
        item.fileType(this.fileItem),
      );
      return currentFileType && currentFileType.icon
        ? currentFileType.icon
        : defaultIcon;
    },
  },
  methods: {
    // 返回为doc、docx类型都为在线文档，其他的为file（非在线的doc、docx）
    isOnlineDoc() {
      return ['docx', 'doc'].some((suff) => this.fileItem.type === suff);
    },
    async sendTemplate() {
      try {
        this.isSending = true;
        //  后端提供上传临时素材得到media_id[限制只支持file类型]，链接：https://developer.work.weixin.qq.com/document/path/90253
        const res = await fileTemplateListApi.getChatMessageMediaid({
          doc_type: this.fileItem.type,
          doc_token: this.fileItem.token,
        });

        // 不支持走原方法
        if (!res.media_id) {
          this.sendTemplateLink(res.url); // url为飞书转为自己的链接，否则分享出去访问不到飞书的文件链接
          return;
        }

        // 判断是否为支持的png/jpg格式
        if (['png', 'jpg'].some((suff) => suff === this.fileNameSuffix)) {
          await this.sendTemplateImage(res.media_id);
          return;
        }

        await this.sendTemplateFile(res.media_id);
      } catch (error) {
        toast.show('文件不存在，请刷新页面');
      } finally {
        this.isSending = false;
      }
    },
    sendTemplateFile(mediaid) {
      return workWxUtils.sendChatMessage({
        msgtype: 'file', // 消息类型，必填
        file: {
          mediaid,
        },
      });
    },
    async sendTemplateImage(mediaid) {
      try {
        await workWxUtils.sendChatMessage({
          msgtype: 'image', // 消息类型，必填
          image: {
            mediaid,
          },
        });
      } catch (e) {
        await this.sendTemplateFile(mediaid).catch(() => Promise.reject());
      }
    },
    sendTemplateLink(url) {
      const text = `点击下方链接，可查看下载【${this.fileItem.name}】:\n\n${url}`;
      workWxUtils.sendChatMessage({
        msgtype: 'text', // 消息类型，必填
        text: {
          content: text,
        },
      });
    },
    async copyLink() {
      await ww.setClipboardData({
        data: this.fileItem.url,
      });
      toast.show('复制成功');
    },
  },
};
</script>

<style lang="less" scoped>
.file-template-item {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px 0;
  &:hover {
    .template-btn-list {
      opacity: 1;
    }
  }
  .template-name {
    flex: 1;
    display: flex;
    align-items: flex-start;
    line-height: 1.5;
    word-break: break-all;
    .label {
      flex-shrink: 0;
      padding: 0 4px;
      height: 20px;
      line-height: 20px;
      background-color: #3fa2f5;
      border-radius: 2px;
      color: white;
      font-size: 12px;
      cursor: pointer;
      margin-left: 6px;
    }
    .template-icon {
      flex-shrink: 0;
      width: 20px;
      margin-right: 4px;
    }
  }

  .template-btn-list {
    flex-shrink: 0;
    opacity: 0;
    margin-left: 10px;
    .btn {
      &.blue {
        border: 1px solid #3fa2f5;
        color: #3fa2f5;
        font-size: 14px;
        cursor: pointer;
        background: white;
        border-radius: 2px;
      }
      &.disabled {
        opacity: 0.65;
        cursor: not-allowed;
      }
    }
  }
}
</style>
